import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Html5Qrcode } from 'html5-qrcode';
import { LanguageService } from 'src/app/services/language.service';
import {
  PickupPerson,
  PickupScanResult,
  PickupService,
} from 'src/app/services/pickup.service';
import { computeQrBoxSize, measureScanHole } from 'src/app/utils/scanner-viewfinder';

type FeedbackKind = 'success' | 'already' | 'error';

interface ScanFeedback {
  kind: FeedbackKind;
  titleKey: string;
  detailName: string;
  className: string;
  message?: string;
}

@Component({
  selector: 'app-pickup-scanner',
  templateUrl: './pickup-scanner.component.html',
  styleUrls: ['./pickup-scanner.component.css'],
})
export class PickupScannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scannerStage') scannerStage?: ElementRef<HTMLElement>;

  private scanner: Html5Qrcode | null = null;
  private scanning = false;
  private starting = false;
  private scanningBusy = false;
  private lastHandledCode = '';
  private lastHandledAt = 0;
  private audioCtx: AudioContext | null = null;
  private classId = '';
  private resizeObserver: ResizeObserver | null = null;
  private onWindowResize = () => this.syncViewfinder();
  readonly scannerElementId = 'pickup-qr-reader';

  cameraActive = false;
  cameraError = '';
  feedback: ScanFeedback | null = null;
  markingBusesReady = false;
  showAllBusesReadyConfirm = false;
  viewfinderStyle: Record<string, string> = {
    top: '15%',
    left: '15%',
    width: '70%',
    height: '70%',
  };

  constructor(
    private pickupService: PickupService,
    private languageService: LanguageService,
    private location: Location,
    private route: ActivatedRoute,
  ) {
    this.classId = String(this.route.snapshot.queryParamMap.get('class') || '');
  }

  async ngAfterViewInit() {
    this.bindStageObserver();
    await this.startCamera();
  }

  async ngOnDestroy() {
    this.unbindStageObserver();
    await this.stopCamera();
    try {
      await this.audioCtx?.close();
    } catch {
      // ignore
    }
    this.audioCtx = null;
  }

  goBack(): void {
    this.location.back();
  }

  async onStartCameraClick(): Promise<void> {
    this.unlockAudio();
    await this.startCamera();
  }

  openAllBusesReadyConfirm(): void {
    if (this.markingBusesReady || this.scanningBusy) {
      return;
    }
    this.showAllBusesReadyConfirm = true;
  }

  async onAllBusesReadyConfirm(confirmed: boolean): Promise<void> {
    if (!confirmed) {
      this.showAllBusesReadyConfirm = false;
      return;
    }
    this.showAllBusesReadyConfirm = false;
    await this.markAllBusesReady();
  }

  async markAllBusesReady(): Promise<void> {
    if (this.markingBusesReady || this.scanningBusy) {
      return;
    }
    this.markingBusesReady = true;
    try {
      const result = await this.pickupService.markAllBusesReady();
      const total = result.counts?.total || 0;
      this.feedback = {
        kind: 'success',
        titleKey: 'PICKUP.ALL_BUSES_READY_OK',
        detailName: String(total),
        className: '',
        message: '',
      };
      this.beep('success');
    } catch (error: any) {
      this.feedback = {
        kind: 'error',
        titleKey: 'PICKUP.ALL_BUSES_READY_FAILED',
        detailName: '',
        className: '',
        message: error?.error?.errorMessages?.[0]?.message || '',
      };
      this.beep('error');
    } finally {
      this.markingBusesReady = false;
    }
  }

  personName(person?: PickupPerson | null): string {
    if (!person) {
      return '—';
    }
    const isAr = this.languageService.current === 'ar';
    const name = isAr
      ? `${person.firstNameAr || ''} ${person.lastNameAr || ''}`.trim()
      : `${person.firstNameEn || ''} ${person.lastNameEn || ''}`.trim();
    return name
      || `${person.firstNameAr || ''} ${person.lastNameAr || ''}`.trim()
      || `${person.firstNameEn || ''} ${person.lastNameEn || ''}`.trim()
      || '—';
  }

  private isInsecureRemoteOrigin(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    if (window.isSecureContext) {
      return false;
    }
    const host = location.hostname;
    return host !== 'localhost' && host !== '127.0.0.1';
  }

  private async startCamera() {
    if (this.starting || this.cameraActive) {
      return;
    }

    this.starting = true;
    this.cameraError = '';

    if (this.isInsecureRemoteOrigin()) {
      this.cameraError = 'ATTENDANCE.HTTPS_REQUIRED';
      this.starting = false;
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'ATTENDANCE.CAMERA_UNSUPPORTED';
      this.starting = false;
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 150));

    const scanConfig = {
      fps: 8,
      qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
        const size = computeQrBoxSize(viewfinderWidth, viewfinderHeight);
        return { width: size, height: size };
      },
      aspectRatio: 1,
      experimentalFeatures: {
        useBarCodeDetectorIfSupported: true,
      },
    };

    const cameraConfigs: Array<string | MediaTrackConstraints> = [
      { facingMode: { ideal: 'environment' } },
      { facingMode: 'environment' },
      { facingMode: { ideal: 'user' } },
      { facingMode: 'user' },
    ];

    for (const cameraConfig of cameraConfigs) {
      try {
        await this.tryStart(cameraConfig, scanConfig);
        this.cameraActive = true;
        this.scanning = true;
        this.starting = false;
        this.queueViewfinderSync();
        return;
      } catch {
        await this.safeResetScanner();
      }
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      await new Promise(resolve => setTimeout(resolve, 350));
      const preferred = cameras.find(camera =>
        /back|rear|environment|خلف|خلفي|wide/i.test(camera.label || '')
      ) || cameras[0];
      if (preferred?.id) {
        await this.tryStart(preferred.id, scanConfig);
        this.cameraActive = true;
        this.scanning = true;
        this.starting = false;
        this.queueViewfinderSync();
        return;
      }
    } catch {
      await this.safeResetScanner();
    }

    this.cameraError = 'ATTENDANCE.CAMERA_FAILED';
    this.cameraActive = false;
    this.scanning = false;
    this.starting = false;
  }

  private async tryStart(
    cameraConfig: string | MediaTrackConstraints,
    scanConfig: {
      fps: number;
      qrbox: (viewfinderWidth: number, viewfinderHeight: number) => { width: number; height: number };
      aspectRatio: number;
      experimentalFeatures: { useBarCodeDetectorIfSupported: boolean };
    },
  ): Promise<void> {
    if (!this.scanner) {
      this.scanner = new Html5Qrcode(this.scannerElementId, { verbose: false });
    }
    await this.scanner.start(
      cameraConfig,
      scanConfig,
      (decoded) => { void this.onCodeDetected(decoded); },
      () => undefined,
    );
  }

  private async safeResetScanner(): Promise<void> {
    if (!this.scanner) {
      return;
    }
    try {
      const state = this.scanner.getState?.();
      if (state === 2 || state === 3 || this.scanning) {
        await this.scanner.stop();
      }
    } catch {
      // ignore
    }
    try {
      await this.scanner.clear();
    } catch {
      // ignore
    }
    this.scanner = null;
    this.scanning = false;
    this.cameraActive = false;
  }

  private async stopCamera() {
    await this.safeResetScanner();
  }

  private bindStageObserver(): void {
    window.addEventListener('resize', this.onWindowResize, { passive: true });
    window.addEventListener('orientationchange', this.onWindowResize, { passive: true });
    const stage = this.scannerStage?.nativeElement;
    if (stage && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncViewfinder());
      this.resizeObserver.observe(stage);
    }
  }

  private unbindStageObserver(): void {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private queueViewfinderSync(): void {
    const delays = [0, 50, 150, 350, 700, 1200];
    for (const ms of delays) {
      setTimeout(() => this.syncViewfinder(), ms);
    }
  }

  private syncViewfinder(): void {
    if (!this.cameraActive) {
      return;
    }
    const stage = this.scannerStage?.nativeElement;
    if (!stage) {
      return;
    }
    const hole = measureScanHole(stage);
    if (!hole) {
      return;
    }
    this.viewfinderStyle = {
      top: `${Math.round(hole.top)}px`,
      left: `${Math.round(hole.left)}px`,
      width: `${Math.round(hole.width)}px`,
      height: `${Math.round(hole.height)}px`,
    };
  }

  private async onCodeDetected(code: string) {
    const trimmed = String(code || '').trim();
    if (!trimmed || this.scanningBusy) {
      return;
    }
    const now = Date.now();
    if (trimmed === this.lastHandledCode && now - this.lastHandledAt < 2500) {
      return;
    }
    this.lastHandledCode = trimmed;
    this.lastHandledAt = now;
    this.scanningBusy = true;
    try {
      const result = await this.pickupService.scan(trimmed, this.classId || undefined);
      this.applyScanResult(result);
    } catch (error: any) {
      this.feedback = {
        kind: 'error',
        titleKey: 'PICKUP.SCAN_FAILED',
        detailName: '',
        className: '',
        message: error?.error?.errorMessages?.[0]?.message || '',
      };
      this.beep('error');
    } finally {
      this.scanningBusy = false;
    }
  }

  private applyScanResult(result: PickupScanResult): void {
    if (result.action === 'bus_arrive') {
      const bus = result.bus || result.buses?.[0]?.bus;
      const label = bus
        ? [bus.number, bus.name].filter(Boolean).join(' - ')
        : '';
      this.feedback = {
        kind: result.alreadyScanned ? 'already' : 'success',
        titleKey: result.alreadyScanned ? 'PICKUP.BUS_ALREADY' : 'PICKUP.BUS_ARRIVE_OK',
        detailName: label,
        className: bus?.driverName || '',
      };
      this.beep(result.alreadyScanned ? 'already' : 'success');
      return;
    }

    if (result.action === 'parent_wait') {
      const names = (result.items || [])
        .map(item => this.personName(item.student))
        .filter(Boolean)
        .join(' · ');
      if (result.alreadyScanned) {
        this.feedback = {
          kind: 'already',
          titleKey: result.reason === 'already_collected'
            ? 'PICKUP.ALREADY_COLLECTED'
            : 'PICKUP.ALREADY_WAITING',
          detailName: names || this.personName(result.parent),
          className: '',
        };
        this.beep('already');
        return;
      }
      this.feedback = {
        kind: 'success',
        titleKey: 'PICKUP.PARENT_WAIT_OK',
        detailName: names || this.personName(result.parent),
        className: this.personName(result.parent),
      };
      this.beep('success');
      return;
    }

    const student = result.student || result.items?.[0]?.student;
    if (result.alreadyScanned) {
      this.feedback = {
        kind: 'already',
        titleKey: 'PICKUP.ALREADY_COLLECTED',
        detailName: this.personName(student),
        className: student?.class?.name || '',
      };
      this.beep('already');
      return;
    }

    this.feedback = {
      kind: 'success',
      titleKey: 'PICKUP.STUDENT_COLLECT_OK',
      detailName: this.personName(student),
      className: student?.class?.name || '',
    };
    this.beep('success');
  }

  private unlockAudio(): void {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) {
        return;
      }
      if (!this.audioCtx) {
        this.audioCtx = new AudioCtx();
      }
      if (this.audioCtx.state === 'suspended') {
        void this.audioCtx.resume();
      }
    } catch {
      // ignore
    }
  }

  private beep(kind: 'success' | 'already' | 'error'): void {
    try {
      this.unlockAudio();
      const ctx = this.audioCtx;
      if (!ctx) {
        return;
      }

      const playTone = (frequency: number, startAt: number, duration: number, volume = 0.08) => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;
        gain.gain.value = 0.0001;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        const t0 = ctx.currentTime + startAt;
        gain.gain.exponentialRampToValueAtTime(volume, t0 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
        oscillator.start(t0);
        oscillator.stop(t0 + duration + 0.02);
      };

      if (kind === 'success') {
        playTone(880, 0, 0.12);
        playTone(1175, 0.12, 0.14);
      } else if (kind === 'already') {
        playTone(420, 0, 0.16);
        playTone(320, 0.18, 0.22);
      } else {
        playTone(220, 0, 0.28, 0.1);
      }
    } catch {
      // ignore audio failures
    }
  }
}
